// FeatureSteps.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Container,
  Avatar,
  useTheme,
  useMediaQuery,
  styled,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import image1 from "../../assets/login.png";
import image2 from "../../assets/ForgotPwd.png";
import image3 from "../../assets/signin.png";
import { motion, AnimatePresence } from "framer-motion";

// Styled components for custom styling
const FeatureCard = styled(Card)(({ theme }) => ({
  height: "100%",
  padding: theme.spacing(3),
  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
  borderRadius: theme.spacing(2),
  transition: "all 0.3s ease-in-out",
  overflow: "hidden",
  position: "relative",
}));

const StepAvatar = styled(Avatar)(({ active, theme, completed }) => ({
  width: 46,
  height: 46,
  transition: "all 0.3s ease",
  backgroundColor:
    completed || active ? theme.palette.primary.main : theme.palette.grey[200],
  color:
    completed || active
      ? theme.palette.primary.contrastText
      : theme.palette.text.secondary,
  border: `2px solid ${
    completed || active ? theme.palette.primary.main : theme.palette.grey[300]
  }`,
  transform: active ? "scale(1.1)" : "scale(1)",
  boxShadow: active ? "0 4px 10px rgba(0, 0, 0, 0.15)" : "none",
}));

const FeatureItem = styled(Box)(({ active, theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1),
  transition: "all 0.3s ease",
  opacity: active ? 1 : 0.6,
  cursor: "pointer",
  "&:hover": {
    opacity: 0.9,
    backgroundColor: theme.palette.grey[50],
  },
}));

export function FeatureSteps({
  features = [
    {
      step: "step 0",
      title: "Intelligent and Natural Human-like Interviews",
      content:
        "AI-powered interviews that feel natural, adaptive, and human-like",
      image: image1,
    },
    {
      step: "step 1",
      title: "Real-Time AI Interviews",
      content:
        "Human-like voice interactions that adapt dynamically to candidate responses",
      image: image2,
    },
    {
      step: "step 2",
      title: "Videos & Voice Analysis",
      content:
        "Advanced scoring for confidence, eye contact, tone, and leadership qualities",
      image: image3,
    },
    {
      step: "step 3",
      title: "Candidate Scoring Dashboards",
      content:
        "Compare candidates across multiple categories with intuitive visualizations",
      image: image1,
    },
    {
      step: "step 4",
      title: "ATS & HRIS Integrations",
      content:
        "Seamless integration with leading HR systems for streamlined workflows",
      image: image2,
    },
  ],
  title = "Features",
  autoPlayInterval = 5000,
}) {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [progress, setProgress] = useState(0);
  const theme = useTheme();

  useEffect(() => {
    const timer = setInterval(() => {
      if (progress < 100) {
        setProgress((prev) => prev + 100 / (autoPlayInterval / 100));
      } else {
        setCurrentFeature((prev) => (prev + 1) % features.length);
        setProgress(0);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [progress, features.length, autoPlayInterval]);

  const handleFeatureClick = (index) => {
    setCurrentFeature(index);
    setProgress(0);
  };

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 6 },
        bgcolor: "background.default",
      }}
    >
      <Container maxWidth="lg">
        <Typography
          variant="h2"
          align="center"
          gutterBottom
          sx={{
            color: "primary.main",
            fontWeight: 600,
            fontSize: { xs: "1.5rem", md: "2rem", lg: "2.5rem" },
            mb: 6,
            position: "relative",
            "&::after": {
              content: '""',
              position: "absolute",
              bottom: -16,
              left: "50%",
              transform: "translateX(-50%)",
              width: 80,
              height: 4,
              backgroundColor: "primary.main",
              borderRadius: 2,
            },
          }}
        >
          {title}
        </Typography>

        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
            <Card
              elevation={0}
              sx={{
                bgcolor: "background.default",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
              }}
            >
              {features.map((feature, index) => (
                <FeatureItem
                  key={index}
                  active={index === currentFeature}
                  onClick={() => handleFeatureClick(index)}
                  sx={{ mb: 2 }}
                >
                  <StepAvatar
                    active={index === currentFeature}
                    completed={index < currentFeature}
                  >
                    {index <= currentFeature ? (
                      <CheckIcon fontSize="small" />
                    ) : (
                      <Typography variant="body2" fontWeight={600}>
                        {index + 1}
                      </Typography>
                    )}
                  </StepAvatar>

                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      fontWeight={600}
                      sx={{
                        mb: 0.5,
                        color:
                          index === currentFeature
                            ? "primary.main"
                            : "text.primary",
                      }}
                    >
                      {feature.title || feature.step}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        lineHeight: 1.6,
                        fontSize: { xs: "0.875rem", md: "1rem" },
                      }}
                    >
                      {feature.content}
                    </Typography>
                  </Box>
                </FeatureItem>
              ))}
            </Card>
          </Grid>
          <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
            <FeatureCard
              sx={{
                p: 0,
                overflow: "hidden",
                background: "transparent",
                boxShadow: "none",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 300, md: 400 },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: {lg:10,md:6, xs:0},
                }}
              >
                <AnimatePresence mode="wait">
                  {features.map(
                    (feature, index) =>
                      index === currentFeature && (
                        <motion.div
                          key={index}
                          initial={{ y: 100, opacity: 0, rotateX: -20 }}
                          animate={{ y: 0, opacity: 1, rotateX: 0 }}
                          exit={{ y: -100, opacity: 0, rotateX: 20 }}
                          transition={{ duration: 0.5, ease: "easeInOut" }}
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <Box
                            component="img"
                            src={feature.image}
                            alt={feature.title}
                            sx={{
                              maxWidth: "100%",
                              maxHeight: "100%",
                              objectFit: "contain",
                              borderRadius: 0,
                            }}
                          />
                        </motion.div>
                      )
                  )}
                </AnimatePresence>
              </Box>
            </FeatureCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}
